// ==========================================
// User & Auth Types (aligned with backend)
// ==========================================

export interface Role {
  id: string
  name: string
  display_name: string
  permissions: string[]
  hierarchy_level: number
  discount_threshold: string
  created_at: string
}

export interface Team {
  id: string
  name: string
  manager_id: string | null
  created_at: string
}

export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  role_id: string
  role: Role | null
  team_id: string | null
  team: Team | null
  is_active: boolean
  attributes: Record<string, unknown>
  created_at: string
  updated_at: string
  phone?: string | null
  avatar_url?: string | null
  totp_enabled?: boolean
}

export interface UserCreate {
  email: string
  first_name: string
  last_name: string
  password: string
  role_id: string
  team_id?: string | null
  is_active?: boolean
  phone: string
  must_change_password?: boolean
  send_welcome_email?: boolean
  attributes?: Record<string, unknown>
}

export interface UserUpdate {
  email?: string
  first_name?: string
  last_name?: string
  role_id?: string
  team_id?: string | null
  is_active?: boolean
  phone?: string | null
  attributes?: Record<string, unknown>
}

export interface PasswordChange {
  current_password?: string
  new_password: string
}

export interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
}

// ==========================================
// Company Types (complete - aligned with backend)
// ==========================================

export interface Company {
  id: string
  name: string
  trading_name: string | null

  // Fiscal identification
  cnpj: string | null
  tax_id: string | null
  tax_id_type: string | null
  inscricao_estadual: string | null
  inscricao_municipal: string | null

  // Receita Federal data (Brazil only)
  natureza_juridica: string | null
  porte: string | null
  capital_social: number | null
  data_abertura: string | null
  situacao_cadastral: string | null
  data_situacao_cadastral: string | null
  cnae_principal: string | null
  cnae_principal_descricao: string | null
  cnaes_secundarios: Record<string, unknown>[]
  simples_nacional: boolean
  mei: boolean
  qsa: QSASocio[]

  // Address
  address: string | null
  neighborhood: string | null
  city: string | null
  state: string | null
  country: string | null
  postal_code: string | null

  // Contact
  phone: string | null
  email: string | null
  website: string | null
  linkedin_url: string | null

  // Segmentation
  industry: string | null
  segment: string | null
  company_size: string | null
  employee_count: number | null
  annual_revenue: string | null

  // Meta
  is_active: boolean
  notes: string | null
  attributes: Record<string, unknown>
  data_source: string | null
  last_cnpj_sync: string | null
  is_brazilian: boolean | null
  fiscal_id: string | null
  created_at: string
  updated_at: string
}

export interface QSASocio {
  nome_socio?: string
  nome?: string
  qualificacao_socio?: string
  qualificacao?: string
}

export interface CompanyCreate {
  name: string
  trading_name?: string | null
  cnpj?: string | null
  tax_id?: string | null
  tax_id_type?: string | null
  inscricao_estadual?: string | null
  inscricao_municipal?: string | null
  address?: string | null
  neighborhood?: string | null
  city?: string | null
  state?: string | null
  country?: string
  postal_code?: string | null
  email?: string | null
  phone?: string | null
  website?: string | null
  linkedin_url?: string | null
  industry?: string | null
  segment?: string | null
  company_size?: string | null
  employee_count?: number | null
  annual_revenue?: string | null
  notes?: string | null
}

export interface ExistingLeadOwnerInfo {
  user_id: string | null
  full_name: string | null
  email: string | null
  phone: string | null
}

export interface ExistingLeadInfo {
  lead_id: string
  title: string | null
  status: string | null
  created_at: string | null
  owner: ExistingLeadOwnerInfo | null
}

export interface CNPJLookupResponse {
  success: boolean
  message: string
  data: Record<string, unknown> | null
  company_exists: boolean
  existing_company_id: string | null
  existing_leads: ExistingLeadInfo[]
}

// ==========================================
// Contact Types (complete - aligned with backend)
// ==========================================

export type ContactRole =
  | 'decision_maker'
  | 'influencer'
  | 'champion'
  | 'user'
  | 'gatekeeper'
  | 'economic_buyer'
  | 'technical_buyer'
  | 'partner'
  | 'partner_admin'
  | 'director'
  | 'president'

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  decision_maker: 'Decisor',
  influencer: 'Influenciador',
  champion: 'Champion',
  user: 'Usuario',
  gatekeeper: 'Gatekeeper',
  economic_buyer: 'Comprador Economico',
  technical_buyer: 'Comprador Tecnico',
  partner: 'Socio',
  partner_admin: 'Socio-Administrador',
  director: 'Diretor',
  president: 'Presidente',
}

export interface Contact {
  id: string
  company_id: string
  first_name: string
  last_name: string | null
  full_name: string | null
  display_name: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  whatsapp: string | null
  linkedin_url: string | null
  instagram: string | null
  twitter: string | null
  job_title: string | null
  website: string | null
  department: string | null
  role: ContactRole | null
  source: string | null
  qsa_qualification: string | null
  is_primary: boolean
  is_active: boolean
  opted_out: boolean
  nps_enabled: boolean
  notes: string | null
  created_at: string
  updated_at: string
  last_contacted_at: string | null
}

export interface ContactCreate {
  company_id: string
  first_name: string
  last_name?: string | null
  email?: string | null
  phone?: string | null
  mobile?: string | null
  whatsapp?: string | null
  linkedin_url?: string | null
  instagram?: string | null
  twitter?: string | null
  job_title?: string | null
  website?: string | null
  department?: string | null
  role?: ContactRole | null
  is_primary?: boolean
  nps_enabled?: boolean
  notes?: string | null
}

export interface ContactUpdate {
  first_name?: string
  last_name?: string | null
  email?: string | null
  phone?: string | null
  mobile?: string | null
  whatsapp?: string | null
  linkedin_url?: string | null
  instagram?: string | null
  twitter?: string | null
  job_title?: string | null
  website?: string | null
  department?: string | null
  role?: ContactRole | null
  is_primary?: boolean
  is_active?: boolean
  opted_out?: boolean
  nps_enabled?: boolean
  notes?: string | null
}

// ==========================================
// Lead Types (aligned with backend)
// ==========================================

export type LeadOrigin =
  | 'bdr_partners'
  | 'active_prospecting'
  | 'website'
  | 'event'

export type LeadStatus =
  | 'suspect'
  | 'prospect'
  | 'mql'
  | 'sql'
  | 'opportunity'
  | 'customer'
  | 'lost'

export interface JourneyProgress {
  id: string
  journey_id: string
  journey_name: string | null
  current_stage_id: string
  stage_name: string | null
  is_active: boolean
  is_completed: boolean
  days_in_stage: number
  sla_deadline: string | null
  is_sla_breached: boolean
}

export interface Lead {
  id: string
  company_id: string
  company: Company | null
  title: string | null
  website: string | null
  primary_contact_id: string | null
  primary_contact: Contact | null
  origin: LeadOrigin
  sub_origin?: string | null
  sub_origin: string | null
  status: LeadStatus
  score: number
  linkedin_post_url: string | null
  linkedin_profile_url: string | null
  partner_name: string | null
  partner_contact: string | null
  internal_consultant_id: string | null
  products_interest: string[]
  budget_confirmed: boolean
  budget_range: string | null
  authority_confirmed: boolean
  authority_contact_id: string | null
  need_confirmed: boolean
  need_description: string | null
  timeline_confirmed: boolean
  timeline_description: string | null
  owner_id: string | null
  owner_name: string | null
  team_id: string | null
  notes: string | null
  attributes: Record<string, unknown>
  is_qualified: boolean
  bant_score: number
  current_stage_name: string | null
  journey_progress: JourneyProgress[]
  created_at: string
  updated_at: string
  qualified_at: string | null
  converted_at: string | null
}

export interface LeadCreate {
  company_id: string
  primary_contact_id?: string | null
  title?: string | null
  website?: string | null
  origin: LeadOrigin
  owner_id?: string | null
  team_id?: string | null
  linkedin_post_url?: string | null
  linkedin_profile_url?: string | null
  partner_name?: string | null
  partner_contact?: string | null
  internal_consultant_id?: string | null
  products_interest?: string[]
  notes?: string | null
  attributes?: Record<string, unknown>
  create_company?: Record<string, unknown> | null
  create_contact?: Record<string, unknown> | null
}

export interface LeadUpdate {
  primary_contact_id?: string | null
  title?: string | null
  website?: string | null
  origin?: LeadOrigin
  sub_origin?: string | null
  status?: LeadStatus
  owner_id?: string | null
  team_id?: string | null
  linkedin_post_url?: string | null
  linkedin_profile_url?: string | null
  partner_name?: string | null
  partner_contact?: string | null
  internal_consultant_id?: string | null
  products_interest?: string[]
  budget_confirmed?: boolean
  budget_range?: string | null
  authority_confirmed?: boolean
  authority_contact_id?: string | null
  need_confirmed?: boolean
  need_description?: string | null
  timeline_confirmed?: boolean
  timeline_description?: string | null
  score?: number
  notes?: string | null
  attributes?: Record<string, unknown>
}

export interface LeadQualify {
  budget_confirmed: boolean
  budget_range?: string | null
  authority_confirmed: boolean
  authority_contact_id?: string | null
  need_confirmed: boolean
  need_description?: string | null
  timeline_confirmed: boolean
  timeline_description?: string | null
  notes?: string | null
}

export interface LeadStats {
  total: number
  by_status: Record<string, number>
  by_origin: Record<string, number>
  by_stage: Record<string, number>
  avg_score: number
  qualified_count: number
  converted_this_month: number
}

// ==========================================
// Opportunity & Kanban Types (aligned with backend)
// ==========================================

export type OpportunityStage = 'qualified' | 'proposal_sent' | 'negotiation' | 'closing' | 'won' | 'lost'

export interface Opportunity {
  id: string
  lead_id: string
  owner_id: string
  contact_id: string | null
  stage: OpportunityStage
  stage_order: number
  name: string
  value: number
  probability: number
  expected_close_date: string | null
  products: { product_id: string; name?: string; quantity: number; unit_price: number }[]
  loss_reason: string | null
  loss_details: string | null
  won_at: string | null
  lost_at: string | null
  created_at: string
  updated_at: string
  lead_company_name: string | null
  lead_company_id: string | null
  lead_company_is_client: boolean | null
  lead_title: string | null
  lead_origin: string | null
  lead_sub_origin: string | null
  owner_name: string | null
  contact_name: string | null
  proposals_count: number
}

export interface OpportunityCreate {
  lead_id: string
  name: string
  value: number
  probability?: number
  expected_close_date?: string | null
  products?: { product_id: string; quantity: number; unit_price: number }[]
  owner_id?: string | null
  contact_id?: string | null
  stage?: OpportunityStage
}

export interface OpportunityUpdate {
  name?: string
  value?: number
  probability?: number
  expected_close_date?: string | null
  products?: { product_id: string; quantity: number; unit_price: number }[]
  owner_id?: string | null
  contact_id?: string | null
  loss_reason?: string | null
  loss_details?: string | null
}

export interface KanbanColumn {
  id: OpportunityStage
  title: string
  color: string
  opportunities: Opportunity[]
  totalValue: number
  count: number
}

// ==========================================
// Catalog Item (Produto/Servico)
// ==========================================

export type CatalogItemType = 'product' | 'service'
export type BillingType = 'one_time' | 'recurring'
export type BillingCycle = 'monthly' | 'quarterly' | 'semiannual' | 'annual'
export type PriceTableStatus = 'draft' | 'active' | 'expired' | 'archived'

export interface CatalogItem {
  id: string
  code: string
  name: string
  description: string | null
  item_type: CatalogItemType
  billing_type: BillingType
  billing_cycle: BillingCycle | null
  category: string | null
  unit: string | null
  is_active: boolean
  attributes: Record<string, unknown>
  created_at: string
  updated_at: string
  description_rich?: string | null
  prices?: Record<string, number> | null
  max_discount_percent?: number | null
  sales_goal_value_annual_brl?: number | null
  sales_goal_year?: number | null
}

export interface CatalogItemCreate {
  code: string
  name: string
  description?: string | null
  item_type: CatalogItemType
  billing_type: BillingType
  billing_cycle?: BillingCycle | null
  category?: string | null
  unit?: string | null
  attributes?: Record<string, unknown>
  description_rich?: string | null
  prices?: Record<string, number> | null
  max_discount_percent?: number | null
  sales_goal_value_annual_brl?: number | null
  sales_goal_year?: number | null
}

export interface CatalogItemUpdate {
  code?: string
  name?: string
  description?: string | null
  item_type?: CatalogItemType
  billing_type?: BillingType
  billing_cycle?: BillingCycle | null
  category?: string | null
  unit?: string | null
  is_active?: boolean
  attributes?: Record<string, unknown>
  description_rich?: string | null
  prices?: Record<string, number> | null
  max_discount_percent?: number | null
  sales_goal_value_annual_brl?: number | null
  sales_goal_year?: number | null
}

// ==========================================
// Price Table
// ==========================================

export interface PriceTableItem {
  id: string
  price_table_id: string
  catalog_item_id: string
  unit_price: number
  min_quantity: number
  max_discount_percent: number
  volume_discounts: { min_qty: number; discount: number }[]
  is_active: boolean
  catalog_item_name?: string | null
  catalog_item_code?: string | null
  catalog_item_type?: string | null
  created_at: string
  updated_at: string
}

export interface PriceTableItemCreate {
  catalog_item_id: string
  unit_price: number
  min_quantity?: number
  max_discount_percent?: number
  volume_discounts?: { min_qty: number; discount: number }[]
}

export interface PriceTableItemUpdate {
  unit_price?: number
  min_quantity?: number
  max_discount_percent?: number
  volume_discounts?: { min_qty: number; discount: number }[]
  is_active?: boolean
}

export interface PriceTable {
  id: string
  name: string
  code: string
  description: string | null
  status: PriceTableStatus
  valid_from: string
  valid_until: string
  segment: string | null
  region: string | null
  currency: string
  is_active: boolean
  created_by: string
  items_count: number
  items: PriceTableItem[]
  created_at: string
  updated_at: string
}

export interface PriceTableCreate {
  name: string
  code: string
  description?: string | null
  status?: PriceTableStatus
  valid_from: string
  valid_until: string
  segment?: string | null
  region?: string | null
  currency?: string
  items?: PriceTableItemCreate[]
}

export interface PriceTableUpdate {
  name?: string
  code?: string
  description?: string | null
  status?: PriceTableStatus
  valid_from?: string
  valid_until?: string
  segment?: string | null
  region?: string | null
  currency?: string
  is_active?: boolean
}

// ==========================================
// Paginated Response
// ==========================================

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  pages: number
}

export interface ApiError {
  detail: string
}

// ==========================================
// Opportunity Activities (Timeline)
// ==========================================

export type ActivityChannel = 'email' | 'whatsapp' | 'phone' | 'meeting' | 'note'

export const CHANNEL_CONFIG: Record<ActivityChannel, { label: string; color: string; bg: string }> = {
  email: { label: 'Email', color: 'text-blue-500', bg: 'bg-blue-500' },
  whatsapp: { label: 'WhatsApp', color: 'text-green-500', bg: 'bg-green-500' },
  phone: { label: 'Ligacao', color: 'text-amber-500', bg: 'bg-amber-500' },
  meeting: { label: 'Reuniao', color: 'text-purple-500', bg: 'bg-purple-500' },
  note: { label: 'Nota', color: 'text-muted-foreground', bg: 'bg-muted-foreground' },
}

export interface OpportunityActivity {
  id: string
  opportunity_id: string
  user_id: string
  channel: ActivityChannel
  content: string
  created_at: string
  updated_at?: string | null
  user_name: string | null
}

// ==========================================
// Proposal Links & Tracking
// ==========================================

export interface ProposalLink {
  id: string
  proposal_id: string
  token: string
  recipient_name: string | null
  recipient_email: string | null
  recipient_phone: string | null
  status: "active" | "expired" | "signed" | "revoked"
  document_hash: string | null
  expires_at: string | null
  created_at: string
  public_url: string | null
  view_count: number
  last_viewed_at: string | null
  is_signed: boolean
}

export interface ProposalView {
  id: string
  ip_address: string | null
  user_agent: string | null
  referrer: string | null
  duration_seconds: number | null
  pages_viewed: number
  viewed_at: string
}

export interface ProposalSignature {
  id: string
  signer_name: string
  signer_email: string | null
  signer_document: string | null
  signer_ip: string | null
  document_hash: string
  signed_at: string
}

export interface ProposalTracking {
  link: ProposalLink
  views: ProposalView[]
  signature: ProposalSignature | null
}

export interface ProposalShareRequest {
  recipient_name: string
  recipient_email?: string
  recipient_phone?: string
  send_email?: boolean
  send_whatsapp?: boolean
  message?: string
  expires_days?: number
}

export interface ProposalShareResponse {
  link: ProposalLink
  public_url: string
  whatsapp_url: string | null
  email_sent: boolean
}
